/**
 * 数据范围管理页面
 */
import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Input, Form, Select,
  Modal, message, Tag, Popconfirm, Tabs, Statistic, Row, Col
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined,
  SafetyOutlined, DatabaseOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getDataScopeList, deleteDataScope, batchDeleteDataScopes,
  getDataPermissionOptions, getResourceTypes, getDataPermissionStatistics
} from '../../api/dataPermission';
import type { DataScopeRule, DataPermissionQueryParams, DataPermissionStatistics } from '../../api/types';
import DataScopeForm from './DataScopeForm';

const { confirm } = Modal;

const DataScopeList: React.FC = () => {
  const [list, setList] = useState<DataScopeRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [currentScope, setCurrentScope] = useState<DataScopeRule | null>(null);
  const [scopeTypeOptions, setScopeTypeOptions] = useState<{ value: string; label: string }[]>([]);
  const [resourceTypes, setResourceTypes] = useState<{ value: string; label: string; fields?: any[] }[]>([]);
  const [statistics, setStatistics] = useState<DataPermissionStatistics | null>(null);
  const [searchForm] = Form.useForm();

  // 获取统计数据
  const fetchStatistics = async () => {
    try {
      const res = await getDataPermissionStatistics();
      if (res?.data) {
        setStatistics(res.data);
      }
    } catch (error) {
      console.error('获取统计数据失败', error);
    }
  };

  // 获取数据范围列表
  const fetchList = async () => {
    setLoading(true);
    try {
      const formValues = searchForm.getFieldsValue();
      const params: DataPermissionQueryParams = {
        ...formValues,
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      const res = await getDataScopeList(params);
      if (res?.data) {
        setList(res.data.list || []);
        setTotal(res.data.total || 0);
      }
    } catch (error) {
      console.error('获取数据范围列表失败', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取选项数据
  const fetchOptions = async () => {
    try {
      const [optionsRes, resourceTypesRes] = await Promise.all([
        getDataPermissionOptions(),
        getResourceTypes(),
      ]);

      const scopeTypes = (optionsRes?.data as any)?.scopeTypes ?? optionsRes?.data?.scope_types;
      if (scopeTypes) {
        setScopeTypeOptions(scopeTypes);
      }

      if (resourceTypesRes?.data?.types) {
        setResourceTypes(resourceTypesRes.data.types.map(t => ({
          value: t.value,
          label: t.label,
          fields: t.fields,
        })));
      }
    } catch (error) {
      console.error('获取选项数据失败', error);
    }
  };

  // 初始化加载
  useEffect(() => {
    fetchList();
    fetchOptions();
    fetchStatistics();
  }, [pagination.current, pagination.pageSize]);

  // 处理表格变更
  const handleTableChange = (paginationInfo: any) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    });
  };

  // 搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchList();
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setPagination({ ...pagination, current: 1 });
    fetchList();
  };

  // 新增
  const handleAdd = () => {
    setCurrentScope(null);
    setFormModalVisible(true);
  };

  // 编辑
  const handleEdit = (record: DataScopeRule) => {
    setCurrentScope(record);
    setFormModalVisible(true);
  };

  // 删除
  const handleDelete = async (id: number) => {
    try {
      await deleteDataScope(id);
      message.success('删除成功');
      fetchList();
    } catch (error) {
      console.error('删除失败', error);
    }
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (!selectedRowKeys.length) {
      message.warning('请至少选择一条记录');
      return;
    }

    confirm({
      title: '确定要删除所选数据范围规则吗?',
      icon: <ExclamationCircleOutlined />,
      content: '此操作将永久删除所选规则，是否继续？',
      onOk: async () => {
        try {
          await batchDeleteDataScopes(selectedRowKeys.map(key => Number(key)));
          message.success('删除成功');
          fetchList();
          setSelectedRowKeys([]);
        } catch (error) {
          console.error('删除失败', error);
        }
      }
    });
  };

  // 表单提交成功的回调
  const handleFormSuccess = () => {
    setFormModalVisible(false);
    fetchList();
  };

  // 获取范围类型颜色
  const getScopeTypeColor = (scopeType: string) => {
    const colorMap: Record<string, string> = {
      all: 'green',
      self: 'blue',
      department: 'orange',
      department_tree: 'purple',
      custom: 'red',
    };
    return colorMap[scopeType] || 'default';
  };

  // 获取范围类型文本
  const getScopeTypeLabel = (scopeType: string) => {
    const found = scopeTypeOptions.find(o => o.value === scopeType);
    return found?.label || scopeType;
  };

  // 获取资源类型文本
  const getResourceTypeLabel = (resourceType: string) => {
    const found = resourceTypes.find(t => t.value === resourceType);
    return found?.label || resourceType;
  };

  const columns: ColumnsType<DataScopeRule> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70,
      fixed: 'left',
    },
    {
      title: '规则名称',
      dataIndex: 'name',
      width: 150,
    },
    {
      title: '资源类型',
      dataIndex: 'resourceType',
      width: 120,
      render: (resourceType) => getResourceTypeLabel(resourceType),
    },
    {
      title: '范围类型',
      dataIndex: 'scopeType',
      width: 120,
      render: (scopeType) => (
        <Tag color={getScopeTypeColor(scopeType)}>
          {getScopeTypeLabel(scopeType)}
        </Tag>
      ),
    },
    {
      title: '角色',
      dataIndex: 'roleName',
      width: 120,
      render: (name) => name || '-',
    },
    {
      title: '用户',
      dataIndex: 'userName',
      width: 100,
      render: (name) => name || '-',
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 1 ? 'success' : 'default'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 160,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除此规则吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="data-scope-list-container">
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic title="数据范围规则" value={statistics.total_scopes} prefix={<SafetyOutlined />} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="字段权限规则" value={statistics.total_field_perms} prefix={<DatabaseOutlined />} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="涉及资源类型" value={Object.keys(statistics.by_resource || {}).length} valueStyle={{ color: '#1890ff' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="范围类型数" value={Object.keys(statistics.by_scope_type || {}).length} valueStyle={{ color: '#3f8600' }} />
            </Card>
          </Col>
        </Row>
      )}
      <Card title="数据范围管理" bordered={false}>
        {/* 搜索表单 */}
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="keyword">
            <Input placeholder="规则名称" allowClear style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="resource_type">
            <Select
              placeholder="资源类型"
              style={{ width: 120 }}
              allowClear
              options={resourceTypes}
            />
          </Form.Item>
          <Form.Item name="scope_type">
            <Select
              placeholder="范围类型"
              style={{ width: 120 }}
              allowClear
              options={scopeTypeOptions}
            />
          </Form.Item>
          <Form.Item name="status">
            <Select
              placeholder="状态"
              style={{ width: 100 }}
              allowClear
            >
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>禁用</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              新增规则
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={!selectedRowKeys.length}
            >
              批量删除
            </Button>
          </Space>
        </div>

        {/* 表格 */}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          pagination={{
            ...pagination,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 数据范围表单对话框 */}
      <DataScopeForm
        visible={formModalVisible}
        scope={currentScope}
        scopeTypeOptions={scopeTypeOptions}
        resourceTypes={resourceTypes}
        onCancel={() => setFormModalVisible(false)}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
};

export default DataScopeList;
